import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox } from "../../components/ui";
import { REQUISITION_KEYS, useLifecycleMutation, useRequisitionQueue, type Requisition } from "../../lib/api";
import { RequisitionTable } from "../recruitment/RequestPosition";
import { SectionCard } from "./shared";

/** Resourcing requests waiting on the signed-in person's level (lifecycle step 2). HR decides on the post first;
 * an executive on the money when the request is large enough; nobody on their own. */
export default function PositionApprovals({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useRequisitionQueue(company || undefined);
  const act = useLifecycleMutation<{ name: string; action: "approve" | "reject"; note: string }>("onesphere.api.requisition.decide", REQUISITION_KEYS);
  const [note, setNote] = useState<Record<string, string>>({});
  // a decided request leaves the queue on refetch; it stays on screen with its outcome until the page is left
  const [done, setDone] = useState<Record<string, { row: Requisition; outcome: string }>>({});
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const d = q.data;
  const rows = [...Object.values(done).map((x) => x.row).filter((r) => !d.to_decide.some((x) => x.name === r.name)), ...d.to_decide];
  const others = d.all_pending.filter((r) => !rows.some((x) => x.name === r.name));
  const decide = (r: Requisition) => done[r.name] ? <span className={`text-xs font-semibold ${done[r.name].outcome === "approved" ? "text-status-green" : "text-status-red"}`} data-req-decided={done[r.name].outcome}>{t(`req.decided.${done[r.name].outcome}`)}</span> : (
    <div className="flex flex-col items-end gap-1">
      <input value={note[r.name] ?? ""} onChange={(e) => setNote((n) => ({ ...n, [r.name]: e.target.value }))} placeholder={t("hr.note")}
        className="w-40 rounded border border-brand-grey-light px-2 py-1 text-xs" data-req-note />
      <div className="flex gap-1">
        <button onClick={() => act.mutate({ name: r.name, action: "approve", note: note[r.name] ?? "" }, { onSuccess: () => setDone((x) => ({ ...x, [r.name]: { row: r, outcome: "approved" } })) })}
          disabled={act.isPending} className="rounded bg-status-green px-2 py-1 text-xs font-semibold text-white disabled:opacity-50" data-req-approve>{t("hr.approve")}</button>
        <button onClick={() => act.mutate({ name: r.name, action: "reject", note: note[r.name] ?? "" }, { onSuccess: () => setDone((x) => ({ ...x, [r.name]: { row: r, outcome: "rejected" } })) })}
          disabled={act.isPending || !(note[r.name] ?? "").trim()} title={t("req.rejectNeedsNote")}
          className="rounded border border-status-red px-2 py-1 text-xs font-semibold text-status-red disabled:opacity-50" data-req-reject>{t("hr.reject")}</button>
      </div>
    </div>
  );
  return (
    <SectionCard title={t("req.approvalsTitle")} aside={rows.length ? t("req.approvalsAside", { n: rows.length }) : t("req.approvalsNone")}>
      <div data-req-approvals>
        {rows.length > 0 && <div className="px-2"><RequisitionTable rows={rows} decide={decide} /></div>}
        {rows.length === 0 && <div className="p-6 text-center text-sm text-brand-grey">{t("req.approvalsNone")}</div>}
        {others.length > 0 && <div className="border-t border-brand-grey-light px-2 pt-2"><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("req.pendingElsewhere")}</div><RequisitionTable rows={others} /></div>}
        {act.isError && <div className="border-t border-brand-grey-light p-3"><ErrorBox error={act.error} /></div>}
      </div>
    </SectionCard>
  );
}
